import constants from '@/config/constants';

class UserManagementService {
  /**
   * Returns the scope tree as flat array of all nodes
   * Optimized for large trees: iterative traversal avoids recursion overhead
   * and repeated concat allocations.
   * @param scopeTree
   * @param parentName - parent scope name for breadcrumb
   * @returns {*}
   */
  flattenScopeTree(scopeTree, parentName = '') {
    if (!scopeTree || scopeTree.length === 0) {
      return [];
    }

    const flatArray = [];
    const stack = [];

    // Seed stack in reverse to preserve original traversal order
    for (let i = scopeTree.length - 1; i >= 0; i--) {
      stack.push({ node: scopeTree[i], parentPath: parentName });
    }

    while (stack.length > 0) {
      const { node, parentPath } = stack.pop();
      const displayName = parentPath
        ? `${parentPath} ⋙ ${node.name}`
        : node.name;

      flatArray.push({
        id: node.id,
        scope: node.scope,
        name: displayName
      });

      const children = node.children;
      if (children && children.length > 0) {
        for (let i = children.length - 1; i >= 0; i--) {
          stack.push({ node: children[i], parentPath: displayName });
        }
      }
    }

    return flatArray;
  }

  /**
   * Returns a mapped scope tree to be used in Selects
   * @param scopeTree
   * @param filterFunction
   * @returns {*[]}
   */
  mapScopeTree(scopeTree, filterFunction) {
    return this.flattenScopeTree(scopeTree)
      .filter((i) => filterFunction(i))
      .map((item) => {
        return {
          title: item.name,
          value: item.id
        };
      })
      .sort((a, b) => {
        return a.value.length - b.value.length;
      });
  }

  /**
   *
   * @param permissions
   * @returns {*[]}
   */
  getFullPermissionsGroupedByRole(permissions) {
    const groupedPermissionsByRole = {};
    const groupedPermissions = [];
    if (permissions) {
      permissions.forEach((permission) => {
        if (permission.role.context === constants.permissionScopes.ADMIN) {
          permission.path = constants.permissionScopes.ADMIN;
          permission.scopeDetails = {
            name: 'Admin'
          };
        }
        if (groupedPermissionsByRole.hasOwnProperty(permission.role.id)) {
          groupedPermissionsByRole[permission.role.id].scopes.push({
            scope: permission.path,
            ...permission.scopeDetails
          });
        } else {
          groupedPermissionsByRole[permission.role.id] = {
            role: permission.role,
            scopes: [
              {
                scope: permission.path,
                ...permission.scopeDetails
              }
            ]
          };
        }
      });
    }

    Object.keys(groupedPermissionsByRole).forEach((key) => {
      return groupedPermissions.push(groupedPermissionsByRole[key]);
    });
    return groupedPermissions;
  }

  getScopeDisplayName(scopeDetails) {
    let scopeDisplayName = '';
    // scopes DC_GROUP, GLOBAL, and SUCO are handled by default
    switch (scopeDetails.scope) {
      case constants.permissionScopes.COUNTRY:
        scopeDisplayName = scopeDetails.name;
        break;
      case constants.permissionScopes.ADMIN:
        scopeDisplayName = scopeDetails.scope;
        break;
      case constants.permissionScopes.APP:
        break;
      default:
        if (scopeDetails) {
          scopeDisplayName = scopeDetails.name;
        } else {
          scopeDisplayName = '';
        }
    }

    return scopeDisplayName;
  }
  /**
   * Checks if the phone number has a valid format, returns a Boolean
   * TODO: Check phone number regEx for better solution, also it looks like that it contains some legacy numbers for testing
   * The country prefixes where updated 2021-11-11, from https://en.wikipedia.org/wiki/List_of_mobile_telephone_prefixes_by_country
   * @param phoneNumber
   * @returns Boolean
   */
  hasPhoneNumberValidFormat(phoneNumber) {
    if (
      !phoneNumber ||
      phoneNumber.substring(1, phoneNumber.length).includes('+') ||
      //max length of the phone number should be 15 excluding the '+'
      phoneNumber.length > 16
    )
      return false;
    const phoneNoFormatRegEx =
      /(\+)(1|7|20|27|30|31|32|33|34|36|39|40|41|43|44|45|46|47|48|49|51|52|53|54|55|56|57|58|60|61|62|63|64|65|66|81|82|84|86|90|91|92|93|94|95|98|211|212|213|216|218|220|221|222|223|224|225|226|227|228|229|230|231|232|233|234|235|236|237|238|239|240|241|242|243|244|245|246|247|248|249|250|251|252|253|254|255|256|257|258|260|261|262|263|264|265|266|267|268|269|290|291|297|298|299|350|351|352|353|354|355|356|357|358|359|370|371|372|373|374|375|376|377|378|380|381|382|383|385|386|387|389|420|421|423|500|501|502|503|504|505|506|507|508|509|590|591|592|593|594|595|596|597|598|599|670|672|673|674|675|676|677|678|679|680|681|682|683|685|686|687|688|689|690|691|692|850|852|853|855|856|880|886|960|961|962|963|964|965|966|967|968|970|971|972|973|974|975|976|977|992|993|994|995|996|998|1242|1246|1264|1268|1284|1340|1345|1441|1473|1649|1664|1670|1671|1684|1721|1758|1767|1784|1787|1809|1829|1849|1868|1869|1876|1939|4779|5999|3906698)(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\d{4,20}$/;
    return phoneNoFormatRegEx.test(phoneNumber);
  }
  validateEmail(email) {
    if (!email) return false;
    const emailFormatRegEx =
      /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    return emailFormatRegEx.test(email);
  }
}

export default new UserManagementService();
